import { User, AdConfig, PaymentGateway, SystemStats, SubscriptionPlan } from '../types';
import { HomepageSettings, CaptchaSettings, EmailOTPSettings, AuthenticationSettings } from '../types';

export const mockEmailOTPSettings: EmailOTPSettings = {
  id: 'email_otp_settings_1',
  deliveryProvider: 'smtp',
  credentials: {
    host: 'smtp.gmail.com',
    port: 587,
    secure: true,
    username: 'noreply@tcards.com',
    password: '••••••••'
  },
  fromName: 'Tcards Security',
  fromEmail: 'noreply@tcards.com',
  codeLength: 6,
  ttlMinutes: 10,
  resendCooldownSeconds: 60,
  maxAttempts: {
    perCode: 5,
    perIP: {
      hourly: 10,
      daily: 50
    },
    perEmail: {
      hourly: 5,
      daily: 20
    }
  },
  lockoutPolicy: {
    enabled: true,
    lockoutMinutes: 15,
    failedAttemptsThreshold: 5
  },
  bindCodeTo: {
    email: true,
    sessionId: true,
    deviceId: false
  },
  template: {
    subject: 'Your Tcards Security Code',
    htmlTemplate: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8fafc;">
        <div style="background-color: #3b82f6; padding: 20px; text-align: center; border-radius: 8px 8px 0 0;">
          <h1 style="color: white; margin: 0; font-size: 24px;">Tcards Security</h1>
        </div>
        <div style="background-color: white; padding: 30px; border-radius: 0 0 8px 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          <h2 style="color: #1f2937; margin-bottom: 20px;">Your Security Code</h2>
          <div style="background-color: #f3f4f6; padding: 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
            <span style="font-size: 32px; font-weight: bold; color: #3b82f6; letter-spacing: 4px;">{{CODE}}</span>
          </div>
          <p style="color: #6b7280; margin-bottom: 20px;">This code will expire in {{TTL}} minutes.</p>
          <p style="color: #6b7280; margin-bottom: 20px;">If you did not request this code, please ignore this email.</p>
          <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">
          <p style="color: #9ca3af; font-size: 12px; text-align: center;">
            Need help? Contact us at {{SUPPORT_CONTACT}}
          </p>
        </div>
      </div>
    `,
    supportContact: 'support@tcards.com',
    brandColors: {
      primary: '#3b82f6',
      secondary: '#1e40af'
    }
  },
  
  postVerifySession: {
    jwtExpiryMinutes: 60,
    refreshTokenExpiryDays: 30
  },
  antiEnumeration: {
    enabled: true,
    genericResponse: 'If an account exists with this email, you will receive a security code shortly.'
  },
  createdAt: '2025-01-13T00:00:00Z',
  updatedAt: '2025-01-13T00:00:00Z'
};

export const mockCaptchaSettings: CaptchaSettings = {
  id: 'captcha_settings_1',
  provider: 'recaptcha_v2',
  siteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // Test key
  secretKey: '6LeIxAcTAAAAAGG-vFI1TnRWxMZNFuojJ4WifJWe', // Test key
  scoreThreshold: 0.5,
  expectedAction: 'login',
  enabledRoutes: ['/auth/request-otp', '/auth/login', '/leads'],
  timeout: 5000,
  retries: 3,
  devBypass: true,
  allowedOrigins: ['localhost:5173', 'e-card-digital-busin-cwc7.bolt.host'],
  auditLogging: true,
  createdAt: '2025-01-13T00:00:00Z',
  updatedAt: '2025-01-13T00:00:00Z'
};

export const defaultAuthenticationSettings: AuthenticationSettings = {
  id: 'auth_settings_1',
  adminAuth: {
    title: 'Admin Access',
    subtitle: 'System administrator login',
    backgroundColor: 'from-red-600 to-red-700',
    textColor: 'text-red-100',
    buttonColor: 'from-red-600 to-red-700 hover:from-red-700 hover:to-red-800',
    enableTwoFA: false,
    enableCaptcha: true,
    fields: [
      {
        name: 'email',
        label: 'Email Address',
        type: 'email',
        placeholder: 'admin@tcards.com',
        required: true
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: 'Enter your password',
        required: true
      }
    ]
  },
  userAuth: {
    signIn: {
      title: 'Welcome Back!',
      subtitle: 'Sign in to access your digital business card',
      backgroundColor: 'from-blue-600 to-indigo-600',
      textColor: 'text-blue-100',
      buttonColor: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
      enableCaptcha: true,
      fields: [
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          placeholder: 'Enter your email address',
          required: true
        },
        {
          name: 'password',
          label: 'Password',
          type: 'password',
          placeholder: 'Enter your password',
          required: true
        }
      ]
    },
    signUp: {
      title: 'Create Your Digital Identity',
      subtitle: 'Join the future of professional networking',
      backgroundColor: 'from-blue-600 to-indigo-600',
      textColor: 'text-blue-100',
      buttonColor: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
      enableImageUpload: true,
  enableCoverUpload: true,
      requireProfilePicture: true,
      usernameField: {
        enabled: true,
        label: 'Username (Card Address)',
        placeholder: 'yourname',
        required: true,
      },
      fields: [
        {
          name: 'name',
          label: 'Full Name',
          type: 'text',
          placeholder: 'John Doe',
          required: true
        },
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          placeholder: 'john@company.com',
          required: true
        },
        {
          name: 'password',
          label: 'Password',
          type: 'password',
          placeholder: 'Create a secure password',
          required: true
        },
        {
          name: 'phone',
          label: 'Phone Number',
          type: 'tel',
          placeholder: '+1 (555) 123-4567',
          required: true
        },
        {
          name: 'position',
          label: 'Job Position',
          type: 'text',
          placeholder: 'Software Engineer',
          required: true
        },
        {
          name: 'company',
          label: 'Company Name',
          type: 'text',
          placeholder: 'Tech Company Inc.',
          required: true
        },
        {
          name: 'gender',
          label: 'Gender',
          type: 'select',
          placeholder: 'Select Gender',
          required: true,
          options: ['male', 'female', 'other', 'prefer_not_to_say']
        },
        {
          name: 'age',
          label: 'Age',
          type: 'select',
          placeholder: 'Select Age Range',
          required: true,
          options: ['18-25', '25-35', '35-45', '45-55', '55+']
        },
        {
          name: 'location',
          label: 'Location',
          type: 'text',
          placeholder: 'New York, USA',
          required: true
        },
        {
          name: 'bio',
          label: 'Bio / About',
          type: 'text',
          placeholder: 'A short professional summary',
          required: false
        }
      ]
    }
  },
  templateSelection: {
    title: 'Choose Your Template',
    subtitle: 'Select a professional design for your digital business card',
    backgroundColor: 'from-blue-50 via-indigo-50 to-purple-50',
    showPopularBadge: true,
    buttonColor: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
  },
  createdAt: '2025-01-13T00:00:00Z',
  updatedAt: '2025-01-13T00:00:00Z'
};

export const mockEmailGateways = [
  {
    id: '1',
    name: 'Gmail SMTP',
    type: 'gmail' as const,
    isActive: true,
    config: {
      host: 'smtp.gmail.com',
      port: 587,
      username: 'admin@tcards.com',
      password: '••••••••',
      fromEmail: 'admin@tcards.com',
      fromName: 'Tcards Team'
    }
  },
  {
    id: '2',
    name: 'SendGrid',
    type: 'sendgrid' as const,
    isActive: false,
    config: {
      apiKey: 'SG.••••••••',
      fromEmail: 'noreply@tcards.com',
      fromName: 'Tcards Digital Cards'
    }
  },
  {
    id: '3',
    name: 'Amazon SES',
    type: 'ses' as const,
    isActive: false,
    config: {
      apiKey: 'AKIA••••••••',
      apiSecret: '••••••••',
      fromEmail: 'support@tcards.com',
      fromName: 'Tcards Support'
    }
  }
];

export const mockSMSGateways = [
  {
    id: '1',
    name: 'Africa\'s Talking',
    type: 'africas_talking' as const,
    isActive: true,
    config: {
      apiKey: 'atsk_••••••••',
      senderId: 'TCARDS'
    }
  },
  {
    id: '2',
    name: 'Twilio',
    type: 'twilio' as const,
    isActive: false,
    config: {
      accountSid: 'AC••••••••',
      authToken: '••••••••',
      senderId: '+1234567890'
    }
  },
  {
    id: '3',
    name: 'Bulk SMS Tanzania',
    type: 'bulk_sms' as const,
    isActive: false,
    config: {
      apiKey: 'bulk_••••••••',
      senderId: 'TCARDS'
    }
  }
];

export const mockLeads = [
  {
    id: 'lead_001',
    cardOwnerId: '1',
    cardOwnerName: 'Daniel S. John',
    fullName: 'Jennifer Martinez',
    email: 'jennifer.martinez@techcorp.com',
    phoneNumber: '+1 (555) 234-5678',
    message: 'Hi Daniel, I saw your profile and would love to discuss potential collaboration opportunities. I work in product management at TechCorp and think we could create something amazing together.',
    submittedAt: '2024-11-20T14:30:00Z',
    source: 'email_form' as const,
    isRead: false,
    isExistingUser: false,
    tags: ['collaboration', 'product_management']
  },
  {
    id: 'lead_002',
    cardOwnerId: '1',
    cardOwnerName: 'Daniel S. John',
    fullName: 'Robert Chen',
    email: 'robert.chen@startup.io',
    phoneNumber: '+1 (555) 345-6789',
    message: 'Hello Daniel, I\'m reaching out regarding a senior developer position at our startup. Your background looks impressive and I\'d love to schedule a call.',
    submittedAt: '2024-11-19T16:45:00Z',
    source: 'email_form' as const,
    isRead: true,
    isExistingUser: false,
    tags: ['job_opportunity', 'startup']
  },
  {
    id: 'lead_003',
    cardOwnerId: '2',
    cardOwnerName: 'Sarah Chen',
    fullName: 'Michael Rodriguez',
    email: 'michael.rodriguez@techflow.com',
    phoneNumber: '+1 (555) 456-7890',
    message: 'Hi Sarah, I saw your marketing work and would love to connect. I\'m also in the tech industry and think we could collaborate.',
    submittedAt: '2024-11-18T10:15:00Z',
    source: 'email_form' as const,
    isRead: false,
    isExistingUser: true,
    existingUserId: '3',
    tags: ['partnership', 'marketing']
  },
  {
    id: 'lead_004',
    cardOwnerId: '4',
    cardOwnerName: 'Emma Wilson',
    fullName: 'Lisa Rodriguez',
    email: 'lisa.rodriguez@brandco.com',
    phoneNumber: '+1 (555) 567-8901',
    message: 'Emma, I love your design portfolio! We have an exciting project that would be perfect for your expertise. Would you be interested in discussing?',
    submittedAt: '2024-11-17T13:20:00Z',
    source: 'email_form' as const,
    isRead: true,
    isExistingUser: false,
    tags: ['design_project', 'portfolio']
  },
  {
    id: 'lead_005',
    cardOwnerId: '1',
    cardOwnerName: 'Daniel S. John',
    fullName: 'Amanda Foster',
    email: 'amanda.foster@consulting.com',
    phoneNumber: '+1 (555) 678-9012',
    message: 'Daniel, I\'m working on a new fintech project and need a technical lead. Your experience with scalable solutions is exactly what we need.',
    submittedAt: '2024-11-16T09:30:00Z',
    source: 'email_form' as const,
    isRead: false,
    isExistingUser: false,
    tags: ['fintech', 'technical_lead']
  },
  {
    id: 'lead_006',
    cardOwnerId: '4',
    cardOwnerName: 'Emma Wilson',
    fullName: 'Alex Thompson',
    email: 'alex.thompson@designstudio.com',
    phoneNumber: '+1 (555) 321-9876',
    message: 'Emma, I\'m a fellow designer and would love to connect. Your work is inspiring!',
    submittedAt: '2024-11-15T11:45:00Z',
    source: 'email_form' as const,
    isRead: false,
    isExistingUser: true,
    existingUserId: '5',
    tags: ['networking', 'design']
  }
];

export const mockUsers: User[] = [
  {
    id: '1',
    name: 'Daniel S. John',
    position: 'Senior Software Engineer',
    company: 'RiccoTech, Inc.',
    avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400',
    coverImage: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800',
    phone: '+1 (555) 123-4567',
    email: 'daniel.john@riccotech.com',
  website: 'https://danieljohn.dev',
    bio: 'Passionate software engineer with 8+ years of experience in full-stack development. Specialized in React, Node.js, and cloud architecture. Love building scalable solutions that make a difference.',
    social: {
      linkedin: 'https://linkedin.com/in/danieljohn',
      twitter: 'https://twitter.com/danieljohn',
      instagram: 'https://instagram.com/danieljohn',
    },
  location: 'San Francisco, CA, USA',
    templateId: '1',
    colors: {
      primary: '#3b82f6',
      secondary: '#1e40af',
      accent: '#f59e0b',
      background: '#f8fafc',
      text: '#1f2937',
      stripLine: '#3b82f6',
      saveButton: '#3b82f6',
      companyBadge: '#8B2635'
    },
    isActive: true,
    subscription: {
      type: 'gold',
      expiresAt: '2024-12-31',
      isActive: true
    },
    analytics: {
      views: 1250,
      saves: 89,
      contactClicks: 156,
      messageClicks: 78,
      socialClicks: 234,
      qrScans: 45,
      shares: 67
    },
    createdAt: '2024-01-15',
    lastActive: '2024-11-20'
  },
  {
    id: '2',
    name: 'Sarah Chen',
    position: 'Marketing Director',
    company: 'InnovateCorp',
    avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400',
    coverImage: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800',
    phone: '+1 (555) 987-6543',
    email: 'sarah.chen@innovatecorp.com',
    website: 'https://sarahchen.marketing',
    bio: 'Creative marketing professional with a passion for brand storytelling and digital innovation. Expert in social media strategy and content creation.',
    social: {
      linkedin: 'https://linkedin.com/in/sarahchen',
      twitter: 'https://twitter.com/sarahchen',
      facebook: 'https://facebook.com/sarahchen',
    },
  location: 'New York, NY, USA',
    templateId: '2',
    colors: {
      primary: '#ec4899',
      secondary: '#be185d',
      accent: '#f97316',
      background: '#fdf2f8',
      text: '#1f2937',
      stripLine: '#ec4899',
      saveButton: '#ec4899',
      companyBadge: '#8B2635'
    },
    isActive: true,
    subscription: {
      type: 'silver',
      expiresAt: '2024-12-15',
      isActive: true
    },
    analytics: {
      views: 890,
      saves: 67,
      contactClicks: 123,
      messageClicks: 56,
      socialClicks: 189,
      qrScans: 34,
      shares: 45
    },
    createdAt: '2024-02-20',
    lastActive: '2024-11-19'
  },
  {
    id: '3',
    name: 'Michael Rodriguez',
    position: 'Product Manager',
    company: 'TechFlow Solutions',
    avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400',
    coverImage: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800',
    phone: '+1 (555) 456-7890',
    email: 'michael.rodriguez@techflow.com',
    website: 'https://michaelrodriguez.pm',
    bio: 'Strategic product manager focused on user experience and data-driven decisions. Leading cross-functional teams to deliver innovative solutions.',
    social: {
      linkedin: 'https://linkedin.com/in/michaelrodriguez',
      twitter: 'https://twitter.com/michaelrodriguez',
    },
  location: 'Austin, TX, USA',
    templateId: '3',
    colors: {
      primary: '#10b981',
      secondary: '#047857',
      accent: '#8b5cf6',
      background: '#f0fdf4',
      text: '#1f2937',
      stripLine: '#10b981',
      saveButton: '#10b981',
      companyBadge: '#8B2635'
    },
    isActive: false,
    subscription: {
      type: 'trial',
      expiresAt: '2024-11-25',
      isActive: false
    },
    analytics: {
      views: 45,
      saves: 12,
      contactClicks: 23,
      messageClicks: 8,
      socialClicks: 34,
      qrScans: 5,
      shares: 7
    },
    createdAt: '2024-11-10',
    lastActive: '2024-11-18'
  },
  {
    id: '4',
    name: 'Emma Wilson',
    position: 'Creative Director',
    company: 'Golden Design Studio',
    avatar: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=400',
    coverImage: 'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800',
    phone: '+1 (555) 789-0123',
    email: 'emma.wilson@goldendesign.com',
    website: 'https://emmawilson.design',
    bio: 'Award-winning creative director with 10+ years of experience in luxury brand design. Passionate about creating memorable visual experiences that captivate and inspire.',
    social: {
      linkedin: 'https://linkedin.com/in/emmawilson',
      twitter: 'https://twitter.com/emmawilson',
      instagram: 'https://instagram.com/emmawilson',
      facebook: 'https://facebook.com/emmawilson',
    },
  location: 'London, UK',
    templateId: '4',
    colors: {
      primary: '#f59e0b',
      secondary: '#d97706',
      accent: '#ea580c',
      background: '#fffbeb',
      text: '#92400e',
      stripLine: '#f59e0b',
      saveButton: '#f59e0b',
      companyBadge: '#8B2635'
    },
    isActive: true,
    subscription: {
      type: 'gold',
      expiresAt: '2024-12-31',
      isActive: true
    },
    analytics: {
      views: 2100,
      saves: 145,
      contactClicks: 298,
      messageClicks: 156,
      socialClicks: 387,
      qrScans: 89,
      shares: 123
    },
    createdAt: '2024-01-05',
    lastActive: '2024-11-20'
  },
  {
    id: '5',
    name: 'Alex Thompson',
    position: 'UX Designer',
    company: 'Design Studio Pro',
    avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400',
    coverImage: 'https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg?auto=compress&cs=tinysrgb&w=800',
    phone: '+1 (555) 321-9876',
    email: 'alex.thompson@designstudio.com',
    website: 'https://alexthompson.design',
    bio: 'Creative UX designer passionate about crafting intuitive digital experiences. Specialized in user research, prototyping, and design systems. Always exploring new ways to solve complex user problems.',
    social: {
      linkedin: 'https://linkedin.com/in/alexthompson',
      twitter: 'https://twitter.com/alexthompson',
      instagram: 'https://instagram.com/alexthompson',
    },
    location: 'Sydney, Australia',
    templateId: '5',
    colors: {
      primary: '#6366f1',
      secondary: '#4338ca',
      accent: '#06b6d4',
      background: '#f1f5f9',
      text: '#1e293b',
      stripLine: '#6366f1',
      saveButton: '#6366f1',
      companyBadge: '#8B2635'
    },
    isActive: true,
    subscription: {
      type: 'silver',
      expiresAt: '2024-12-20',
      isActive: true
    },
    analytics: {
      views: 567,
      saves: 34,
      contactClicks: 89,
      messageClicks: 45,
      socialClicks: 123,
      qrScans: 23,
      shares: 34
    },
    createdAt: '2024-03-10',
    lastActive: '2024-11-20'
  }
];

export const mockAds: AdConfig[] = [
  {
    // Align mock ad id with server fallback ad id for unified tracking endpoints
    id: 'fallback-1',
    image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400',
    title: 'Premium Business Tools',
    description: 'Boost your productivity with our enterprise solutions',
    link: 'https://example.com/business-tools',
    targetAudience: {
      ageRange: ['25-35', '35-45'],
      gender: ['male', 'female', 'other'], // was ['all']
      location: ['US', 'Canada'],
      jobTitles: ['Manager', 'Director', 'Engineer']
    },
    isActive: true,
    schedule: {
      startDate: '2024-11-01',
      endDate: '2024-12-31',
      startTime: '09:00',
      endTime: '18:00',
      timezone: 'UTC'
    },
    clicks: 234,
    redirects: 189,
    analytics: {
      views: 15420,
      clicks: 234,
      impressions: 18500,
      demographics: {
        ageGroups: {
          '25-35': { views: 8200, clicks: 145 },
          '35-45': { views: 5800, clicks: 78 },
          '45-55': { views: 1420, clicks: 11 }
        },
        genders: {
          'male': { views: 9200, clicks: 140 },
          'female': { views: 6220, clicks: 94 }
        },
        locations: {
          'US': { views: 12000, clicks: 180 },
          'Canada': { views: 3420, clicks: 54 }
        },
        jobTitles: {
          'Manager': { views: 6800, clicks: 98 },
          'Director': { views: 4200, clicks: 76 },
          'Engineer': { views: 4420, clicks: 60 }
        }
      },
      dailyStats: {
        '2024-11-15': { views: 420, clicks: 8 },
        '2024-11-16': { views: 380, clicks: 6 },
        '2024-11-17': { views: 450, clicks: 9 }
      },
      conversionRate: 1.52
    },
    createdAt: '2024-11-01T09:00:00Z',
    updatedAt: '2024-11-17T14:30:00Z'
  },
  {
    // Align second mock id with server fallback
    id: 'fallback-2',
    image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=400',
    title: 'Marketing Automation',
    description: 'Streamline your marketing campaigns',
    link: 'https://example.com/marketing',
    targetAudience: {
      ageRange: ['25-35', '35-45'],
      gender: ['all'],
      location: ['US', 'UK'],
      jobTitles: ['Marketing', 'Sales']
    },
    isActive: true,
    schedule: {
      startDate: '2024-11-01',
      endDate: '2024-12-31',
      startTime: '08:00',
      endTime: '20:00',
      timezone: 'UTC'
    },
    clicks: 156,
    redirects: 134,
    analytics: {
      views: 8900,
      clicks: 156,
      impressions: 10200,
      demographics: {
        ageGroups: {
          '25-35': { views: 5200, clicks: 89 },
          '35-45': { views: 3700, clicks: 67 }
        },
        genders: {
          'male': { views: 4200, clicks: 72 },
          'female': { views: 4700, clicks: 84 }
        },
        locations: {
          'US': { views: 6200, clicks: 108 },
          'UK': { views: 2700, clicks: 48 }
        },
        jobTitles: {
          'Marketing': { views: 5400, clicks: 94 },
          'Sales': { views: 3500, clicks: 62 }
        }
      },
      dailyStats: {
        '2024-11-15': { views: 280, clicks: 5 },
        '2024-11-16': { views: 320, clicks: 7 },
        '2024-11-17': { views: 290, clicks: 4 }
      },
      conversionRate: 1.75
    },
    createdAt: '2024-11-01T08:00:00Z',
    updatedAt: '2024-11-17T16:45:00Z'
  }
];

export const mockSubscriptionPlans: SubscriptionPlan[] = [
  {
    id: 'silver',
    name: 'Silver Plan',
    price: '$4',
    monthlyPrice: 4,
    description: 'Perfect for professionals',
    features: [
      'Unlimited card views',
      'Mini website publishing included',
      'Custom colors & branding',
      'QR code generation',
      'Basic analytics dashboard',
      'Email support',
      'All 5 professional templates'
    ],
    buttonText: 'Get Started',
    buttonColor: 'bg-gray-600 hover:bg-gray-700',
    isPopular: false,
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2025-01-06T00:00:00Z'
  },
  {
    id: 'gold',
    name: 'Gold Plan',
    price: '$8',
    monthlyPrice: 8,
    description: 'For growing businesses',
    features: [
      'Everything in Silver',
      'Own domain (www.yourdomain.com)',
      'Advanced analytics & insights',
      'Lead tracking & management',
      'Professional PDF reports',
      'Broadcast messaging',
      'Priority support'
    ],
    buttonText: 'Get Started',
    buttonColor: 'from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700',
    isPopular: true,
    isActive: true,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2025-01-06T00:00:00Z'
  }
];

export const mockPaymentGateways: PaymentGateway[] = [
  {
    id: '1',
    name: 'M-Pesa',
    type: 'mpesa',
    isActive: true,
    config: {
      apiKey: 'mpesa_api_key',
      merchantId: 'merchant_123'
    }
  },
  {
    id: '2',
    name: 'Tigo Pesa',
    type: 'tigopesa',
    isActive: true,
    config: {
      apiKey: 'tigo_api_key',
      merchantId: 'tigo_merchant_456'
    }
  },
  {
    id: '3',
    name: 'Airtel Money',
    type: 'airtel_money',
    isActive: true,
    config: {
      apiKey: 'airtel_api_key',
      merchantId: 'airtel_merchant_789'
    }
  },
  {
    id: '4',
    name: 'Visa Card',
    type: 'visa',
    isActive: true,
    config: {
      apiKey: 'visa_api_key',
      secretKey: 'visa_secret_key'
    }
  },
  {
    id: '5',
    name: 'Google Pay',
    type: 'google_pay',
    isActive: false,
    config: {
      apiKey: 'google_pay_api_key'
    }
  },
  {
    id: '6',
    name: 'Stripe',
    type: 'stripe',
    isActive: true,
    config: {
      publishableKey: 'pk_test_••••••••',
      secretKey: 'sk_test_••••••••',
      webhookSecret: 'whsec_••••••••'
    }
  }
];

// Comprehensive list of countries for location selection
export const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo',
  'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica',
  'Dominican Republic', 'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea',
  'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia',
  'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait',
  'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
  'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia',
  'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis',
  'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Saudi Arabia',
  'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka',
  'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia',
  'Zimbabwe'
];

// Lightweight city list for autocomplete (common global cities + key Tanzanian cities)
export const cities = [
  // Tanzania
  'Dar es Salaam', 'Dodoma', 'Arusha', 'Mwanza', 'Mbeya', 'Moshi', 'Tanga', 'Morogoro', 'Zanzibar City', 'Mtwara', 'Iringa', 'Tabora', 'Kigoma', 'Songea', 'Shinyanga',
  // Africa (selected)
  'Nairobi', 'Mombasa', 'Kampala', 'Kigali', 'Lusaka', 'Harare', 'Maputo', 'Johannesburg', 'Cape Town', 'Durban', 'Gaborone', 'Accra', 'Kumasi', 'Lagos', 'Abuja', 'Port Harcourt', 'Cairo', 'Alexandria', 'Tunis', 'Casablanca', 'Rabat', 'Algiers',
  // Middle East
  'Dubai', 'Abu Dhabi', 'Doha', 'Riyadh', 'Jeddah', 'Muscat', 'Amman', 'Beirut',
  // Europe
  'London', 'Manchester', 'Birmingham', 'Paris', 'Lyon', 'Marseille', 'Berlin', 'Munich', 'Hamburg', 'Madrid', 'Barcelona', 'Valencia', 'Rome', 'Milan', 'Naples', 'Amsterdam', 'Rotterdam', 'Brussels', 'Vienna', 'Zurich', 'Geneva', 'Stockholm', 'Oslo', 'Copenhagen', 'Helsinki', 'Dublin', 'Lisbon', 'Prague', 'Warsaw', 'Budapest', 'Athens',
  // Americas
  'New York', 'Los Angeles', 'Chicago', 'Houston', 'San Francisco', 'Seattle', 'Boston', 'Miami', 'Atlanta', 'Dallas', 'Toronto', 'Vancouver', 'Montreal', 'Mexico City', 'Guadalajara', 'Bogotá', 'Lima', 'Santiago', 'Buenos Aires', 'Rio de Janeiro', 'São Paulo',
  // Asia-Pacific
  'Tokyo', 'Osaka', 'Seoul', 'Busan', 'Beijing', 'Shanghai', 'Shenzhen', 'Guangzhou', 'Hong Kong', 'Taipei', 'Singapore', 'Kuala Lumpur', 'Jakarta', 'Bangkok', 'Hanoi', 'Ho Chi Minh City', 'Manila', 'Sydney', 'Melbourne', 'Auckland'
];

// Mapping of common cities to their countries for validation in signup
// Keys are lowercased city names for case-insensitive lookup
export const cityCountryMap: Record<string, string> = {
  // Tanzania
  'dar es salaam': 'Tanzania', 'dodoma': 'Tanzania', 'arusha': 'Tanzania', 'mwanza': 'Tanzania', 'mbeya': 'Tanzania', 'moshi': 'Tanzania', 'tanga': 'Tanzania', 'morogoro': 'Tanzania', 'zanzibar city': 'Tanzania', 'mtwara': 'Tanzania', 'iringa': 'Tanzania', 'tabora': 'Tanzania', 'kigoma': 'Tanzania', 'songea': 'Tanzania', 'shinyanga': 'Tanzania',
  // Africa (selected)
  'nairobi': 'Kenya', 'mombasa': 'Kenya', 'kampala': 'Uganda', 'kigali': 'Rwanda', 'lusaka': 'Zambia', 'harare': 'Zimbabwe', 'maputo': 'Mozambique', 'johannesburg': 'South Africa', 'cape town': 'South Africa', 'durban': 'South Africa', 'gaborone': 'Botswana', 'accra': 'Ghana', 'kumasi': 'Ghana', 'lagos': 'Nigeria', 'abuja': 'Nigeria', 'port harcourt': 'Nigeria', 'cairo': 'Egypt', 'alexandria': 'Egypt', 'tunis': 'Tunisia', 'casablanca': 'Morocco', 'rabat': 'Morocco', 'algiers': 'Algeria',
  // Middle East
  'dubai': 'United Arab Emirates', 'abu dhabi': 'United Arab Emirates', 'doha': 'Qatar', 'riyadh': 'Saudi Arabia', 'jeddah': 'Saudi Arabia', 'muscat': 'Oman', 'amman': 'Jordan', 'beirut': 'Lebanon',
  // Europe
  'london': 'United Kingdom', 'manchester': 'United Kingdom', 'birmingham': 'United Kingdom', 'paris': 'France', 'lyon': 'France', 'marseille': 'France', 'berlin': 'Germany', 'munich': 'Germany', 'hamburg': 'Germany', 'madrid': 'Spain', 'barcelona': 'Spain', 'valencia': 'Spain', 'rome': 'Italy', 'milan': 'Italy', 'naples': 'Italy', 'amsterdam': 'Netherlands', 'rotterdam': 'Netherlands', 'brussels': 'Belgium', 'vienna': 'Austria', 'zurich': 'Switzerland', 'geneva': 'Switzerland', 'stockholm': 'Sweden', 'oslo': 'Norway', 'copenhagen': 'Denmark', 'helsinki': 'Finland', 'dublin': 'Ireland', 'lisbon': 'Portugal', 'prague': 'Czech Republic', 'warsaw': 'Poland', 'budapest': 'Hungary', 'athens': 'Greece',
  // Americas
  'new york': 'United States', 'los angeles': 'United States', 'chicago': 'United States', 'houston': 'United States', 'san francisco': 'United States', 'seattle': 'United States', 'boston': 'United States', 'miami': 'United States', 'atlanta': 'United States', 'dallas': 'United States', 'toronto': 'Canada', 'vancouver': 'Canada', 'montreal': 'Canada', 'mexico city': 'Mexico', 'guadalajara': 'Mexico', 'bogotá': 'Colombia', 'bogota': 'Colombia', 'lima': 'Peru', 'santiago': 'Chile', 'buenos aires': 'Argentina', 'rio de janeiro': 'Brazil', 'são paulo': 'Brazil', 'sao paulo': 'Brazil',
  // Asia-Pacific
  'tokyo': 'Japan', 'osaka': 'Japan', 'seoul': 'South Korea', 'busan': 'South Korea', 'beijing': 'China', 'shanghai': 'China', 'shenzhen': 'China', 'guangzhou': 'China', 'hong kong': 'China', 'taipei': 'Taiwan', 'singapore': 'Singapore', 'kuala lumpur': 'Malaysia', 'jakarta': 'Indonesia', 'bangkok': 'Thailand', 'hanoi': 'Vietnam', 'ho chi minh city': 'Vietnam', 'manila': 'Philippines', 'sydney': 'Australia', 'melbourne': 'Australia', 'auckland': 'New Zealand'
};

export const mockSystemStats: SystemStats = {
  totalUsers: 1250,
  activeUsers: 890,
  newUsersToday: 23,
  totalCards: 1250,
  activeCards: 890,
  totalSubscriptions: 567,
  silverSubscriptions: 234,
  goldSubscriptions: 333,
  trialUsers: 123,
  expiredTrials: 45,
  totalViews: 125000,
  totalShares: 8900,
  totalAdClicks: 2340,
  totalAdRedirects: 1890,
  demographics: {
    ageGroups: {
      '18-25': 234,
      '25-35': 456,
      '35-45': 345,
      '45-55': 156,
      '55+': 59
    },
    genders: {
      'male': 678,
      'female': 456,
      'other': 116
    },
    locations: {
      'US': 567,
      'Canada': 234,
      'UK': 189,
      'Germany': 123,
      'Other': 137
    }
  }
};

export const templates = [
  {
    id: '1',
    name: 'Professional Classic',
    description: 'Clean and professional design with gradient header',
  preview: 'Classic business card style',
  previewImage: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    id: '2',
    name: 'Elegant Looking',
    description: 'Sophisticated design with elegant typography',
  preview: 'Refined and polished appearance',
  previewImage: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    id: '3',
    name: 'Modern and Clean',
    description: 'Contemporary minimalist design',
  preview: 'Fresh and modern aesthetic',
  previewImage: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    id: '4',
    name: 'Golden View',
    description: 'Luxury golden theme with premium styling',
  preview: 'Elegant golden business card design',
  previewImage: 'https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=600'
  },
  {
    id: '5',
    name: 'Classic Modular',
    description: 'Modular component-based design with clean sections',
  preview: 'Professional modular layout with enhanced interactions',
  previewImage: 'https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg?auto=compress&cs=tinysrgb&w=600'
  }
];

export const defaultHomepageSettings: HomepageSettings = {
  id: 'homepage_settings_1',
  branding: {
    siteName: 'Tcards',
    logoText: 'T',
    tagline: 'Digital Business Cards, Mini Websites & CRM'
  },
  // Maintenance / Coming Soon page settings (admin-toggleable)
  maintenance: {
    enabled: false,
    title: 'We\'ll be back soon',
    subtitle: 'Our site is currently undergoing scheduled maintenance',
    content: 'We\'re making some improvements to give you a better experience. Thank you for your patience.',
    // ISO timestamp for countdown target; admins can update
    targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    // Background visuals
    background: {
      gradient: 'from-blue-50 via-indigo-50 to-purple-50',
      imageUrl: '',
    },
    // Show only admin login/signup entry points while enabled
    showAdminButtons: true,
    // Optional custom button labels
    adminLoginText: 'Admin Login',
    adminSignupText: '',
  },
  // Static pages defaults
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How we collect, use, and protect your data',
    contentHtml: `
      <h2>Introduction</h2>
      <p>This Privacy Policy explains how Tcards ("Tcards", "we", "us", or "our") collects, uses, discloses, and protects your information when you access or use our website, applications (including the Tcards Sales mobile app), and services (collectively, the "Services").</p>

      <h2>Information We Collect</h2>
      <h3>Information You Provide</h3>
      <ul>
        <li>Account details (name, email, password, username)</li>
        <li>Profile and business card content (avatar, cover image, bio, links, social accounts)</li>
        <li>Communications (messages, support requests, feedback)</li>
        <li>Payment-related info (processed by third-party gateways; we do not store full card details)</li>
      </ul>
      <h3>Information Collected Automatically</h3>
      <ul>
        <li>Usage data (pages viewed, clicks, interactions, referring/exit pages)</li>
        <li>Device and log data (IP address, browser type, operating system)</li>
        <li>Cookies and similar technologies (for session management, analytics, and preferences)</li>
      </ul>

      <h2>Location Data</h2>
      <p>Our Tcards Sales mobile application accesses and collects <strong>location data</strong>, including <strong>precise location</strong> (GPS) and, where available, <strong>approximate location</strong>.</p>
      <ul>
        <li><strong>What we collect:</strong> latitude and longitude, accuracy, timestamps, and a reverse-geocoded place name derived from your GPS coordinates.</li>
        <li><strong>When we collect it:</strong> while you are signed in as a field sales user, including when the app is in use and, with your permission, while the app is in the <strong>background</strong> (for example, via an Android foreground service notification for field tracking).</li>
        <li><strong>How often:</strong> location is sampled periodically (typically about every five minutes) and also when you check in or check out at a customer stop on your route.</li>
        <li><strong>Why we collect it:</strong> to support CRM field activity reporting; verify route visits (check-in / check-out against assigned stop locations); enable managers to view live or recent seller locations on maps; and improve route compliance and operational reporting for your organization.</li>
        <li><strong>Who can see it:</strong> location reports are stored on our servers and made available to authorized users in your organization (such as managers and administrators) through the Tcards CRM. We do not sell your location data.</li>
        <li><strong>Your choices:</strong> location access is requested via the device permission prompts. You can revoke location permission in your device settings; doing so may limit or disable field-tracking and route check-in features while you remain signed in.</li>
      </ul>
      <p>We do not use location data for advertising or unrelated third-party marketing.</p>

      <h2>How We Use Information</h2>
      <ul>
        <li>Operate, maintain, and improve the Services</li>
        <li>Provide CRM and field sales features, including location-based route verification and activity reporting</li>
        <li>Personalize experiences and content</li>
        <li>Provide support and respond to inquiries</li>
        <li>Monitor performance, detect, prevent, and address security incidents or abuse</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>Legal Bases (EEA/UK)</h2>
      <p>Where applicable, we process personal data under the following legal bases: performance of a contract, legitimate interests, compliance with legal obligations, and consent (where required), including consent for device location permissions where required by platform or law.</p>

      <h2>Sharing of Information</h2>
      <ul>
        <li><strong>Your organization:</strong> If you use Tcards Sales or CRM as part of a company account, location and visit data may be shared with authorized managers and administrators in that organization for operational purposes.</li>
        <li><strong>Service Providers:</strong> We share data with vendors performing services on our behalf (e.g., hosting, analytics, email delivery, payment processing, mapping/geocoding) under data protection agreements.</li>
        <li><strong>Legal & Safety:</strong> We may disclose information to comply with laws, enforce our terms, or protect rights, safety, and property.</li>
        <li><strong>Business Transfers:</strong> In mergers, acquisitions, or asset sales, data may be transferred subject to this Policy.</li>
      </ul>

      <h2>Data Retention</h2>
      <p>We retain information for as long as necessary to provide the Services, comply with legal obligations, resolve disputes, and enforce agreements. Location and visit reports are retained for CRM reporting and audit needs for your organization and may be deleted or anonymized when no longer required or upon valid account-deletion requests, subject to legal retention requirements. Retention periods vary by data type and usage.</p>

      <h2>Your Rights</h2>
      <p>Depending on your region, you may have rights to access, correct, delete, restrict, or object to processing, and to data portability. You may also withdraw consent where processing is based on consent, including by changing location permissions on your device.</p>

      <h2>Security</h2>
      <p>We use administrative, technical, and organizational safeguards designed to protect information. No method of transmission or storage is 100% secure.</p>

      <h2>Children’s Privacy</h2>
      <p>Our Services are not intended for children under 13 (or as defined by local law). We do not knowingly collect personal data from children.</p>

      <h2>International Transfers</h2>
      <p>Information may be processed in countries other than your own. We implement appropriate safeguards for international transfers where required.</p>

      <h2>Changes to this Policy</h2>
      <p>We may update this Policy from time to time. We will post the updated version with the effective date. Your continued use of the Services indicates acceptance.</p>

      <h2>Contact Us</h2>
      <p>Questions or requests regarding this Policy: <a href="mailto:support@tcards.com">support@tcards.com</a></p>
    `,
    images: [],
    seo: { title: 'Privacy Policy', description: 'How Tcards collects, uses, and protects your data, including location data used by the Tcards Sales app.', keywords: ['privacy','policy','tcards','data protection','location','GPS'], canonicalUrl: '', robots: 'index, follow' }
  },
  terms: {
    title: 'Terms of Service',
    subtitle: 'Terms that govern your use of the service',
    contentHtml: `
      <h2>Acceptance of Terms</h2>
      <p>By creating an account, accessing, or using Tcards (the "Service"), you agree to these Terms of Service (the "Terms"). If you do not agree, do not use the Service.</p>

      <h2>Eligibility & Accounts</h2>
      <ul>
        <li>You must be at least the age of majority in your jurisdiction to use the Service.</li>
        <li>You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.</li>
        <li>You must provide accurate and complete information and keep it up to date.</li>
      </ul>

      <h2>Use of the Service</h2>
      <ul>
        <li>Do not misuse, interfere with, or disrupt the Service or attempt unauthorized access.</li>
        <li>Do not upload unlawful, harmful, infringing, defamatory, or otherwise objectionable content.</li>
        <li>Comply with all applicable laws and regulations.</li>
      </ul>

      <h2>User Content & Licenses</h2>
      <p>You retain ownership of content you submit or display. You grant Tcards a worldwide, non-exclusive, royalty-free license to host, store, reproduce, display, and distribute such content solely to provide and improve the Service.</p>

      <h2>Subscriptions, Payments & Refunds</h2>
      <ul>
        <li>Certain features may require a paid subscription. Fees, billing cycles, and taxes are disclosed at purchase.</li>
        <li>Unless stated otherwise, subscriptions renew automatically until canceled.</li>
        <li>Refunds are handled in accordance with our refund policy (if any) and applicable law.</li>
      </ul>

      <h2>Intellectual Property</h2>
      <p>Tcards and its licensors own all right, title, and interest in and to the Service, including software, trademarks, and content (excluding your content). You may not copy, modify, or create derivative works except as permitted by law or expressly authorized.</p>

      <h2>Third-Party Services</h2>
      <p>The Service may integrate with third-party services. Your use of those services is subject to their terms and policies. We are not responsible for third-party services.</p>

      <h2>Termination</h2>
      <p>We may suspend or terminate access immediately for any violation of these Terms or to protect the Service or other users. You may stop using the Service at any time. Upon termination, certain provisions survive (e.g., IP, disclaimers, limitation of liability).</p>

      <h2>Disclaimers</h2>
      <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>

      <h2>Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, Tcards will not be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenues, data, or use.</p>

      <h2>Indemnification</h2>
      <p>You agree to indemnify and hold Tcards harmless from any claims arising out of your use of the Service or violation of these Terms.</p>

      <h2>Governing Law</h2>
      <p>These Terms are governed by applicable laws in your jurisdiction unless a specific jurisdiction is stated elsewhere in our policies or agreements.</p>

      <h2>Changes to Terms</h2>
      <p>We may modify these Terms at any time. We will post the updated Terms with the effective date. Continued use constitutes acceptance.</p>

      <h2>Contact</h2>
      <p>Questions about these Terms: <a href="mailto:support@tcards.com">support@tcards.com</a></p>
    `,
    images: [],
    seo: { title: 'Terms of Service', description: 'Terms governing your use of Tcards.', keywords: ['terms','service','tcards'], canonicalUrl: '', robots: 'index, follow' }
  },
  hero: {
    headline: 'Your All-in-One Digital Business Platform',
    subheadline: 'Create stunning digital business cards, launch mini websites for your products and services, and manage customer relationships — all from one powerful platform.',
    ctaButtonText: 'Get Started Free',
    ctaButtonColor: 'from-blue-600 to-indigo-600'
  },
  about: {
    title: 'About Tcards',
    subtitle: 'More Than Just Digital Business Cards',
    badge: 'The Complete Business Growth Platform',
    description: 'Tcards is a unified digital platform that empowers entrepreneurs, freelancers, and businesses of all sizes with professional digital business cards, instant mini websites, and a built-in CRM — everything you need to establish your online presence, attract customers, and grow revenue.',
    benefits: [
      {
        title: 'Digital Business Cards',
        description: 'Design and share interactive digital business cards with QR codes, analytics, and one-tap contact saving. Stand out with 5 professional templates and real-time engagement tracking.',
        icon: 'Smartphone'
      },
      {
        title: 'Mini Website Builder',
        description: 'Launch a product or service website in minutes — no coding required. Accept orders, showcase your catalog, and reach customers with a beautiful, mobile-first storefront.',
        icon: 'Store'
      },
      {
        title: 'Built-in CRM',
        description: 'Track leads, manage contacts, and nurture customer relationships from one dashboard. Turn every card view and website visit into a business opportunity.',
        icon: 'Briefcase'
      }
    ]
  },
  mission: {
    title: 'Our Mission',
    subtitle: 'Empowering Digital Growth',
    description: 'To give every professional and business — from solo entrepreneurs to growing teams — the digital tools they need to build their brand, connect with customers, and scale with confidence.',
    backgroundColor: 'from-slate-900 via-blue-900 to-indigo-900',
    textColor: 'text-white',
    innovationPanel: {
      title: 'Innovation',
      description: 'From digital business cards to instant mini websites and integrated CRM, we continuously ship features that keep your business ahead of the curve — so you can focus on what you do best.',
      learnMoreText: 'Explore our platform'
    },
    excellencePanel: {
      title: 'Reliability',
      description: 'Enterprise-grade infrastructure with 99.9% uptime, bank-level data security, and a product built for speed. Your brand deserves a platform you can count on every day.',
      learnMoreText: 'Our commitment'
    },
    stats: [
      { value: '50K+', label: 'Active Users' },
      { value: '1M+', label: 'Cards Shared' },
      { value: '99.9%', label: 'Uptime' },
      { value: '24/7', label: 'Support' }
    ]
  },
  features: {
    title: 'Everything You Need to Grow Your Business',
    subtitle: 'Digital cards, mini websites, and CRM tools — one platform, unlimited potential',
    backgroundColor: 'bg-gray-50',
    features: [
      {
        title: 'Interactive Digital Business Cards',
        description: 'Choose from 5 premium templates, add QR codes, social links, and one-tap contact saving. Share your card via link, NFC, or SMS — and track every view in real time.',
        icon: 'Palette',
        color: 'text-blue-600'
      },
      {
        title: 'Mini Website Builder',
        description: 'Create a beautiful product or service website in minutes with 8 ready-made templates. List products, accept orders, and customize branding — zero coding required.',
        icon: 'Store',
        color: 'text-emerald-600'
      },
      {
        title: 'CRM & Lead Management',
        description: 'Track leads from card views and website visits automatically. Manage contacts, tag prospects, and follow up — all from a single, intuitive dashboard.',
        icon: 'Briefcase',
        color: 'text-purple-600'
      },
      {
        title: 'Advanced Analytics & Insights',
        description: 'Monitor card views, clicks, QR scans, shares, and website orders with rich charts and date filters. Understand what drives engagement and double down on it.',
        icon: 'BarChart3',
        color: 'text-green-600'
      },
      {
        title: 'Payments & Earnings',
        description: 'Accept payments through your mini website with integrated payment processing. Track earnings, manage payouts, and download PDF reports on demand.',
        icon: 'CreditCard',
        color: 'text-orange-600'
      },
      {
        title: 'Enterprise-Grade Security',
        description: 'Role-based access control, OTP verification, encrypted data, and CAPTCHA protection. Your data and your customers\' data are safe with bank-level security.',
        icon: 'Shield',
        color: 'text-indigo-600'
      }
    ]
  },
  pricing: {
    title: 'Simple, Transparent Pricing',
    subtitle: 'Choose the plan that fits your professional needs',
    backgroundColor: 'bg-white',
    // Fallback plans aligned with typical server SubscriptionPlan pricing (silver/gold/platinum)
    // These are only used when server-provided plans are unavailable; kept minimal & similar.
    plans: [
      {
        name: 'Silver',
        price: '$9.99',
        description: 'Professional essentials for individuals',
        features: [
          'Unlimited card views',
          'Custom branding & colors',
          'QR code generation',
          'Basic analytics dashboard'
        ],
        buttonText: 'Start Silver',
        buttonColor: 'bg-gray-600 hover:bg-gray-700',
        isPopular: false
      },
      {
        name: 'Gold',
        price: '$19.99',
        description: 'Advanced features & growth tools',
        features: [
          'Everything in Silver',
          'Advanced analytics & insights',
          'Lead tracking & management',
          'Broadcast messaging'
        ],
        buttonText: 'Go Gold',
        buttonColor: 'from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700',
        isPopular: true
      },
      {
        name: 'Platinum',
        price: '$39.99',
        description: 'Enterprise scale & team controls',
        features: [
          'Everything in Gold',
          'Company domains & SSO',
          'Advanced team controls',
          'Priority SLA support'
        ],
        buttonText: 'Go Platinum',
        buttonColor: 'from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700',
        isPopular: false
      }
    ]
  },
  partners: {
    title: 'Trusted by Leading Companies',
    subtitle: 'Join thousands of professionals using Tcards',
    backgroundColor: 'bg-gray-50',
    logos: [
      { name: 'Microsoft', imageUrl: '/assets/partners/microsoft.svg', website: 'https://microsoft.com' },
      { name: 'Google', imageUrl: '/assets/partners/google.svg', website: 'https://google.com' },
      { name: 'Apple', imageUrl: '/assets/partners/apple.svg', website: 'https://apple.com' },
      { name: 'Amazon', imageUrl: '/assets/partners/amazon.svg', website: 'https://amazon.com' },
      { name: 'Netflix', imageUrl: '/assets/partners/netflix.svg', website: 'https://netflix.com' },
      { name: 'Tesla', imageUrl: '/assets/partners/tesla.svg', website: 'https://tesla.com' }
    ]
  },
  contact: {
    title: 'Get In Touch',
    subtitle: 'Ready to transform your networking? Contact us today.',
    backgroundColor: 'bg-white',
    recipientEmail: 'admin@tcards.com',
    fields: [
      { name: 'name', label: 'Your Name', type: 'text', required: true, placeholder: 'Your Name' },
      { name: 'email', label: 'Your Email', type: 'email', required: true, placeholder: 'Your Email' },
      { name: 'phone', label: 'Phone Number', type: 'tel', required: true, placeholder: 'Phone Number' },
      { name: 'location', label: 'Location', type: 'text', required: true, placeholder: 'Location' },
      { name: 'message', label: 'Your Message', type: 'textarea', required: true, placeholder: 'Your Message' }
    ],
    submitButtonText: 'Send Message',
    submitButtonColor: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
  },
  footer: {
    backgroundColor: 'bg-gray-900',
    textColor: 'text-white',
    companyDescription: 'Your all-in-one platform for digital business cards, mini websites, and CRM. Build your brand, reach more customers, and grow your business — all in one place.',
    adminButtonColor: 'from-red-600 to-red-700 hover:from-red-700 hover:to-red-800',
    userButtonColor: 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700',
    adminButtonText: 'Admin',
    userButtonText: 'Card User',
    copyright: '© 2025 Tcards. All rights reserved. Powered by RiccoTech, Inc.',
    links: [
      {
        category: 'Quick Links',
        items: [
          { label: 'About', url: '#about' },
          { label: 'Features', url: '#features' },
          { label: 'Pricing', url: '#pricing' },
          { label: 'Contact', url: '#contact' }
        ]
      },
      {
        category: 'Support',
        items: [
          { label: 'FAQ', url: '/faq' },
          { label: 'Privacy Policy', url: '/privacy' },
          { label: 'Terms of Service', url: '/terms' },
          { label: 'Refund Policy', url: '/refund-policy' },
          { label: 'Blog', url: '/blog' }
        ]
      }
    ]
  },
  // Optional FAQ content used by the public FAQ page
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Answers to common questions about our digital business cards platform',
    items: [
      { question: 'What is Tcards?', answer: 'Tcards lets you create, customize, and share professional digital business cards that work everywhere.' },
      { question: 'How do I share my card?', answer: 'Share your unique link, QR code, or NFC-enabled device to let people view your card instantly.' },
      { question: 'Can I update my card after sharing?', answer: 'Yes. Update your details anytime—changes reflect immediately without reprinting anything.' }
    ]
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    headingFontSize: {
      h1: 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl',
      h2: 'text-2xl sm:text-3xl md:text-4xl',
      h3: 'text-lg sm:text-xl'
    },
    bodyFontSize: 'text-base sm:text-lg'
  },
  previews: {
    site: {
      title: 'Tcards — Digital Business Cards, Mini Websites & CRM',
      description: 'Your all-in-one digital business platform. Create professional business cards, launch mini websites, and manage customers — all from one place.',
      imageUrl: ''
    },
    card: {
      titleTemplate: '{name} — Digital Business Card',
      descriptionTemplate: 'View and save {name}\'s Tcards—powered digital business card.'
    },
    digitalCard: {
      title: 'TCards Digital Business Card | Your Professional Digital Identity',
      description:
        'Create a professional TCards Digital Business Card with your bio, contact information, social profiles, business links and more. Choose a template, personalize your TCard and share it anywhere.',
      imageUrl: '',
      keywords: ['Digital Business Card', 'Digital Card', 'Online Business Card', 'Professional Digital Card', 'Virtual Business Card'],
    },
    templates: {
      title: 'Choose Your TCard | TCards Templates',
      description:
        'Browse Business and Artist TCard templates. Pick a design that matches your style, then personalize your digital card in minutes.',
      imageUrl: '',
      keywords: ['TCards Templates', 'Digital Business Card Templates', 'Business Card Design'],
    },
    contact: {
      title: 'Contact TCards',
      description: 'Have a question, need support or want to learn more about TCards? Get in touch with us.',
      imageUrl: '',
      keywords: ['Contact TCards', 'TCards Support'],
    },
  }
};